import { apiSlice } from "../../api/apiSlice";
import { persistor } from "../../store";
import {
  logoutUser,
  setCredentials,
  updateFeedback,
} from "./authSlice";
import { teardownSocket } from "../../../utils/socket";

export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // @desc: Login with credentials
    login: builder.mutation({
      query: (credentials) => ({
        url: "api/auth/login",
        method: "POST",
        body: { ...credentials },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!data?.data?.accessToken) return;
          dispatch(setCredentials(data.data));
          // Resume persistence after a prior logout pause().
          persistor.persist();
        } catch (error) {
          console.error("error", error);
        }
      },
    }),

    verifyLoginOtp: builder.mutation({
      query: (body) => ({
        url: "api/auth/login/otp",
        method: "POST",
        body,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!data?.data?.accessToken) return;
          dispatch(setCredentials(data.data));
          persistor.persist();
        } catch (error) {
          console.error("error", error);
        }
      },
    }),

    submitProductFeedback: builder.mutation({
      query: (body) => ({
        url: "/api/user/feedback",
        method: "POST",
        body,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(updateFeedback());
        } catch (error) {
          console.error("error", error);
        }
      },
    }),

    // @desc: Logout on server, clear local state and reset apiSlice state.
    sendLogout: builder.mutation({
      query: () => ({
        url: "api/auth/logout",
        method: "POST",
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          // Still clear local session if the API is unreachable.
        } finally {
          teardownSocket();
          // Pause so later Redux resets (theme/auth/api) do not rewrite persist:root.
          persistor.pause();
          dispatch(logoutUser());
          await persistor.purge();
          dispatch(apiSlice.util.resetApiState());
          // Catch any in-flight writers (e.g. alpha_screens) that raced the clear.
          localStorage.clear();
          sessionStorage.clear();
        }
      },
    }),
    forgotPassword: builder.mutation({
      query: (credentials) => ({
        url: "api/auth/forgot-password",
        method: "POST",
        body: { ...credentials },
      }),
    }),

    updateProfile: builder.mutation({
      query: (data) => ({
        url: `/api/auth/my-profile`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Profile"],
    }),
    resetPassword: builder.mutation({
      query: (credentials) => ({
        url: `api/auth/reset-password/${credentials.id}/${credentials.token}`,
        method: "PATCH",
        body: credentials.password,
      }),
    }),
    getCompletedAlphaScreens: builder.query({
      query: () => ({
        url: "api/user/alpha-screens/complete",
        method: "GET",
      }),
      async onQueryStarted(arg, { getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Skip write if the user already logged out while this request was in flight.
          if (!getState().auth.token) return;
          localStorage.setItem(
            "alpha_screens",
            JSON.stringify(data?.data || [])
          );
        } catch (err) {
          // Ignore; logout clears storage separately.
        }
      },
    }),
    markAlphaScreenComplete: builder.mutation({
      query: (data) => ({
        url: "api/user/alpha-screens/complete",
        method: "POST",
        body: data,
      }),
    }),
    changePassword: builder.mutation({
      query: (data) => ({
        url: "/api/auth/change-password",
        method: "PATCH",
        body: data,
      }),
    }),
    changeProfileImage: builder.mutation({
      query: (data) => ({
        url: "/api/auth/avatar",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["User"],
    }),
    removeProfileImage: builder.mutation({
      query: () => ({
        url: "/api/auth/avatar",
        method: "DELETE",
      }),
      invalidatesTags: ["User"],
    }),
    getProfile: builder.query({
      query: () => ({
        url: `/api/auth/my-profile`,
        method: "GET",
      }),
      providesTags: ["User"],
    }),
  }),
});

export const {
  useLoginMutation,
  useVerifyLoginOtpMutation,
  useSendLogoutMutation,
  useForgotPasswordMutation,
  useUpdateProfileMutation,
  useResetPasswordMutation,
  useGetProfileQuery,
  useSubmitProductFeedbackMutation,
  useChangePasswordMutation,
  useChangeProfileImageMutation,
  useRemoveProfileImageMutation,
  useGetCompletedAlphaScreensQuery,
  useMarkAlphaScreenCompleteMutation,
} = authApiSlice;
